import React, { useMemo, useCallback } from 'react';
import { StaticContext } from 'react-router';
import { Container } from 'vendors/semantic-ui';

import { Filter, MultiFilterValue, SimpleFilterValue } from 'modules/shared/models';
import { useRouter } from 'modules/shared/hooks';
import { CheckboxList } from 'modules/shared-ui/components';
import { Accordion, TagList, TagProps, DualSlider } from 'modules/shared-ui-advanced/components';
import { CollectionPicker } from '../collection-picker';
import { ProductFilterListProps } from './product-filter-list-type';

import styles from './product-filter-list-component.module.css';

export const ProductFilterList: React.FC<ProductFilterListProps> = ({
    filters,
    handleFiltersSelection,
}: ProductFilterListProps) => {
    const { history, location } = useRouter<{}, StaticContext, { activeProductFilters?: number[] }>();

    const selectedFilters: TagProps[] = useMemo(() => {
        const datas = filters.filter(f => f.values && (f.values as MultiFilterValue[]).some(v => v.isSelected));
        if (!datas || datas.length === 0) {
            return [];
        }

        return datas
            .map(d =>
                (d.values as MultiFilterValue[])
                    .filter(v => v.isSelected)
                    .map<TagProps>(v => {
                        return {
                            title: v.displayName,
                            value: `${d.id}_${v.value}`,
                        };
                    }),
            )
            .reduce((acc, val) => acc.concat(val), []);
    }, [filters]);

    const handleDeleteFilter = useCallback(
        (tagValue: string) => {
            if (tagValue) {
                const vals = tagValue.split('_');
                const filter = filters.find(f => f.id === vals[0]);
                if (filter && filter.values) {
                    const value = filter.values.find(v => v.value === Number(vals[1]));
                    if (value) {
                        (value as MultiFilterValue).isSelected = false;
                        handleFiltersSelection(filters);
                    }
                }
            }
        },
        [filters, handleFiltersSelection],
    );

    const handleDeleteAllFilters = useCallback(() => {
        handleFiltersSelection([]);
    }, [handleFiltersSelection]);

    const onActiveIndexsChange = useCallback(
        activeIndexs => {
            history.replace({ ...location, state: { ...location.state, activeProductFilters: activeIndexs } });
        },
        [location, history],
    );

    const activeProductFilters =
        location.state && location.state.activeProductFilters ? location.state.activeProductFilters : undefined;

    if (filters && filters.length > 0) {
        const collectionFilter: Filter | undefined = filters.find(f => f.type === 4);
        const minPriceFilter: Filter | undefined = filters.find(f => f.type === 5);
        const maxPriceFilter: Filter | undefined = filters.find(f => f.type === 6);
        return (
            <React.Fragment>
                <div className={styles.container}>
                    <TagList
                        tags={selectedFilters}
                        onDeleteTagClick={handleDeleteFilter}
                        onDeleteAllClick={handleDeleteAllFilters}
                    />
                    {(() => {
                        if (!collectionFilter) {
                            return <React.Fragment />;
                        }

                        const selectedCollection = collectionFilter.values.find(
                            c => (c as MultiFilterValue).isSelected,
                        );
                        return (
                            <React.Fragment>
                                <Container className={styles.collectionContainer}>
                                    <CollectionPicker
                                        collections={collectionFilter.values}
                                        selectedValue={selectedCollection ? selectedCollection.value : 0}
                                        onChange={selectedValue => {
                                            for (let i = 0; i < collectionFilter.values.length; i += 1) {
                                                const currentValue = collectionFilter.values[i] as MultiFilterValue;
                                                currentValue.isSelected = currentValue.value === selectedValue;
                                            }
                                            handleFiltersSelection(filters);
                                        }}
                                    />
                                </Container>

                                <div className={styles.horizontalDivider} />
                            </React.Fragment>
                        );
                    })()}
                    <Accordion
                        activeIndexs={activeProductFilters}
                        onActiveIndexsChange={onActiveIndexsChange}
                        defaultOpened
                        items={filters
                            .filter(
                                f =>
                                    f &&
                                    f.values &&
                                    // f.values.length > 1 &&
                                    f.type !== 4 &&
                                    f.type !== 5 &&
                                    f.type !== 6,
                            )
                            .map((filter, index) => {
                                const component = (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <div key={index} className={styles.accordionContainer}>
                                        <CheckboxList<number, MultiFilterValue>
                                            values={(filter.values as MultiFilterValue[]).sort((a, b) => {
                                                if (b.displayName.toUpperCase() === 'AUTRES') {
                                                    return -1;
                                                }

                                                return a.displayName.localeCompare(b.displayName);
                                            })}
                                            onSelectionChange={() => {
                                                if (handleFiltersSelection) {
                                                    handleFiltersSelection(filters);
                                                }
                                            }}
                                            renderLabel={value => `${value.displayName} (${value.count})`}
                                        />
                                    </div>
                                );

                                return {
                                    component,
                                    title: filter.type === 3 ? 'Marques' : filter.displayName,
                                };
                            })}
                    />
                </div>
                <div className={styles.horizontalDivider} />
                {minPriceFilter &&
                    minPriceFilter.values &&
                    minPriceFilter.values.length > 0 &&
                    maxPriceFilter &&
                    maxPriceFilter.values &&
                    maxPriceFilter.values.length > 0 && (
                        <div className={styles.priceContainer}>
                            <DualSlider
                                minValue={minPriceFilter.values[0].value}
                                selectedMinValue={
                                    (minPriceFilter.values[0] as SimpleFilterValue).selectedValue ||
                                    minPriceFilter.values[0].value
                                }
                                maxValue={maxPriceFilter.values[0].value}
                                selectedMaxValue={
                                    (maxPriceFilter.values[0] as SimpleFilterValue).selectedValue ||
                                    maxPriceFilter.values[0].value
                                }
                                onChange={(minValue: number, maxValue: number) => {
                                    (minPriceFilter.values[0] as SimpleFilterValue).selectedValue = minValue;
                                    (maxPriceFilter.values[0] as SimpleFilterValue).selectedValue = maxValue;

                                    if (handleFiltersSelection) {
                                        handleFiltersSelection(filters);
                                    }
                                }}
                                unit="€"
                            />
                        </div>
                    )}
            </React.Fragment>
        );
    }

    return <React.Fragment />;
};
